'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetPositionsRow, positionEntityQueryKey, type QueryKey, useActiveWallet, useAuth, useGetPositions, useWallets } from '@morfi/backend-sdk-v2/client';
import { useState } from 'react';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { isClosedDustResidual } from '@/lib/market/position-visibility';
import { POSITION_FIELDS } from '@/lib/sdk/projections';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
export interface UseMarketPositionsArgs {
  /** User pubkey (REST key) — self or another account. */
  userId: string;
  /** Every wallet to roll up (realtime fan-out). */
  walletPubkeys: string[];
  /** Scope to one market (market-detail / trade form) — omit for a winner-mint scope. */
  market?: string;
  /** Scope to specific outcome mints (winner page). */
  outcomeMints?: string[];
}
export interface UseMarketPositionsResult {
  /** Live rows (hold-last-guarded), before the dust-residual filter. */
  rows: readonly GetPositionsRow[];
  /** `rows` minus fully-exited dust-residual ghosts (MOR-619) — what the PnL card renders. */
  visibleRows: readonly GetPositionsRow[];
  queryKey: QueryKey;
  isPending: boolean;
  isError: boolean;
}
export interface OwnedMarketPositionScope {
  readonly userId: string;
  readonly walletPubkeys: string[];
  readonly activeWalletPubkey: string | null;
  readonly isLoading: boolean;
}

/** One stable all-owned-wallet scope shared by every market-detail consumer. */
export function useOwnedMarketPositionScope() {
  const $ = _c(16);
  const {
    isAuthenticated
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    wallets,
    isLoading
  } = useWallets();
  const {
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const usablePrimary = primaryWallet !== null && primaryWallet.archivedAt === null && primaryWallet.isAvailable !== false ? primaryWallet : null;
  if (!multiWalletEnabled) {
    const primaryWalletPubkey = isAuthenticated ? usablePrimary?.address ?? null : null;
    const t0 = !isLoading && primaryWalletPubkey !== null ? primaryWalletPubkey : "";
    let t1;
    if ($[0] !== isLoading || $[1] !== primaryWalletPubkey) {
      t1 = !isLoading && primaryWalletPubkey !== null ? [primaryWalletPubkey] : [];
      $[0] = isLoading;
      $[1] = primaryWalletPubkey;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== isLoading || $[4] !== primaryWalletPubkey || $[5] !== t0 || $[6] !== t1) {
      t2 = {
        userId: t0,
        walletPubkeys: t1,
        activeWalletPubkey: primaryWalletPubkey,
        isLoading
      };
      $[3] = isLoading;
      $[4] = primaryWalletPubkey;
      $[5] = t0;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const activeWalletPubkey = isAuthenticated ? activeWallet?.address ?? null : null;
  const ready = activeWalletPubkey !== null && !isLoading;
  let t0;
  if ($[8] !== ready || $[9] !== wallets) {
    t0 = ready ? [...new Set(wallets.map(_temp))].sort(_temp2) : [];
    $[8] = ready;
    $[9] = wallets;
    $[10] = t0;
  } else {
    t0 = $[10];
  }
  const walletPubkeys = t0;
  const t1 = ready ? activeWalletPubkey : "";
  let t2;
  if ($[11] !== activeWalletPubkey || $[12] !== isLoading || $[13] !== t1 || $[14] !== walletPubkeys) {
    t2 = {
      userId: t1,
      walletPubkeys,
      activeWalletPubkey,
      isLoading
    };
    $[11] = activeWalletPubkey;
    $[12] = isLoading;
    $[13] = t1;
    $[14] = walletPubkeys;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}

/**
 * THE canonical viewer-positions read for a market surface (MOR-852) — shared
 * by the trade form and the PnL position card so both consume the SAME cache
 * entry, the SAME realtime subscription set, and therefore always show the
 * same holdings data. Encapsulates:
 *
 * - The `useGetPositions` call with the one canonical `POSITION_FIELDS`
 *   projection (a superset of every consumer's needs — a narrower `fields`
 *   list would key a second cache entry and duplicate fetch + NATS traffic).
 *   Rows carry the server's `pnl` breakdown, so every consumer selects the
 *   same gross view via `buildPnlBreakdown`/`selectPnlView` instead of
 *   deriving its own accounting.
 * - `marketPubkeys` (plural) scopes the canonical backend read to the market;
 *   backend filters it as `wp.market = ANY`. Pool movement is deliberately
 *   not folded into these rows: executable value comes from the separate
 *   atomic on-chain pool snapshot.
 * - `useLiveListData`, so same-tab optimistic position updates and the
 *   authoritative post-trade reconciliation are observed by every consumer.
 * - The hold-last-non-empty guard (MOR-626): `includeZero: 'true'` means a
 *   position that exists always returns a row, so once rows have shown,
 *   `liveRows` emptying can only be a re-key / refetch artifact — never a
 *   genuine "position gone". Consumers must not blank during a refetch, so
 *   the last non-empty rows are held across the swap. The guard is SCOPE-KEYED: a wallet /
 *   market / mint switch is a genuine scope change, so held rows reset
 *   instead of leaking the previous scope's positions.
 */
function _temp2(left, right) {
  return left.localeCompare(right);
}
function _temp(wallet) {
  return wallet.address;
}
export function useMarketPositions(t0) {
  const $ = _c(35);
  const {
    userId,
    walletPubkeys,
    market,
    outcomeMints
  } = t0;
  const {
    isAuthenticated
  } = useAuth();
  const hasAuthenticatedViewer = isAuthenticated && userId.length > 0 && walletPubkeys.length > 0;
  const authenticatedUserId = hasAuthenticatedViewer ? userId : "";
  let t1;
  if ($[0] !== hasAuthenticatedViewer || $[1] !== walletPubkeys) {
    t1 = hasAuthenticatedViewer ? walletPubkeys : [];
    $[0] = hasAuthenticatedViewer;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const authenticatedWalletPubkeys = t1;
  let t2;
  if ($[3] !== market) {
    t2 = market !== undefined ? {
      marketPubkeys: [market]
    } : {};
    $[3] = market;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== outcomeMints) {
    t3 = outcomeMints !== undefined ? {
      outcomeMints
    } : {};
    $[5] = outcomeMints;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [...POSITION_FIELDS];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== authenticatedUserId || $[9] !== authenticatedWalletPubkeys || $[10] !== t2 || $[11] !== t3) {
    t5 = {
      userId: authenticatedUserId,
      walletPubkeys: authenticatedWalletPubkeys,
      ...t2,
      ...t3,
      includeZero: "true",
      fields: t4
    };
    $[8] = authenticatedUserId;
    $[9] = authenticatedWalletPubkeys;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== hasAuthenticatedViewer) {
    t6 = {
      enabled: hasAuthenticatedViewer
    };
    $[13] = hasAuthenticatedViewer;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const query = useGetPositions(t5, t6);
  const liveRows = useLiveListData(query.queryKey, query.rows, positionEntityQueryKey);
  let t7;
  if ($[15] !== authenticatedWalletPubkeys) {
    t7 = authenticatedWalletPubkeys.join(",");
    $[15] = authenticatedWalletPubkeys;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = market ?? "";
  let t9;
  if ($[17] !== outcomeMints) {
    t9 = outcomeMints?.join(",") ?? "";
    $[17] = outcomeMints;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== authenticatedUserId || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = [authenticatedUserId, t7, t8, t9];
    $[19] = authenticatedUserId;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const scope = t10.join("|");
  let t11;
  if ($[24] !== liveRows || $[25] !== scope) {
    t11 = {
      scope,
      rows: liveRows
    };
    $[24] = liveRows;
    $[25] = scope;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const [held, setHeld] = useState(t11);
  if (held.scope !== scope || liveRows.length > 0 && !samePositionRows(liveRows, held.rows)) {
    setHeld({
      scope,
      rows: liveRows
    });
  }
  const heldRows = held.scope === scope ? held.rows : liveRows;
  const rows = liveRows.length > 0 ? liveRows : heldRows;
  let t12;
  if ($[27] !== rows) {
    t12 = rows.filter(_temp3);
    $[27] = rows;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const visibleRows = t12;
  const t13 = hasAuthenticatedViewer && query.isPending && rows.length === 0;
  let t14;
  if ($[29] !== query.isError || $[30] !== query.queryKey || $[31] !== rows || $[32] !== t13 || $[33] !== visibleRows) {
    t14 = {
      rows,
      visibleRows,
      queryKey: query.queryKey,
      isPending: t13,
      isError: query.isError
    };
    $[29] = query.isError;
    $[30] = query.queryKey;
    $[31] = rows;
    $[32] = t13;
    $[33] = visibleRows;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}
function _temp3(r) {
  return !isClosedDustResidual(r);
}
function samePositionRows(left: readonly GetPositionsRow[], right: readonly GetPositionsRow[]): boolean {
  return left.length === right.length && left.every((row, index) => {
    const previous = right[index];
    return previous !== undefined && POSITION_FIELDS.every(field => row[field] === previous[field]);
  });
}